import {
  Button,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import InputField from '../elements/InputField';
import { roleOptions } from '../../static/common';
import { useUserProjects } from '../../query/apiQueries';
import MultiSelectDropdown from '../elements/MultiSelectDropdown';


const AddUserForm = ({ handleSubmit, loading }) => {

  const { data: projectData } = useUserProjects({
    perPage: 'all',
    page: 1,
    search: null,
    includeTestCases: false,
    includeLinkedProjects: true,
  });

  const projectOptions =
    projectData?.projects?.map((p) => ({
      id: p.id,
      name: p.name,
      jiraCloudId: p.jira_cloud_id,
    })) || [];

  return (
    <Formik
      initialValues={{
        first_name: '',
        last_name: '',
        email: '',
        role: 'team-member',
        projects: [],
      }}
      validationSchema={Yup.object().shape({
        first_name: Yup.string().max(255).required('First Name is required'),
        last_name: Yup.string().max(255),
        email: Yup.string()
          .email('Must be a valid email')
          .max(255)
          .required('Email is required'),
        role: Yup.string().required('Role is required'),
      })}
      onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
        try {
          const formattedValues = {
            ...values,
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            email: values.email.trim(),
          };
          await handleSubmit(formattedValues);
          setStatus({ success: false });
          setSubmitting(false);
        } catch (err) {
          console.error(err);
          setStatus({ success: false });
          setErrors({ submit: err.message });
          setSubmitting(false);
        }
      }}>
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        touched,
        values,
        setFieldValue,
      }) => (
        <form noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <InputField
                label="First Name"
                id="first_name"
                type="text"
                value={values.first_name}
                name="first_name"
                fullWidth
                required
                placeholder="First"
                error={Boolean(touched.first_name && errors.first_name)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputField
                label="Last Name"
                id="last_name"
                type="text"
                value={values.last_name}
                name="last_name"
                fullWidth
                placeholder="Last"
                error={Boolean(touched.last_name && errors.last_name)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <InputField
                label="Email Address"
                id="email"
                type="email"
                value={values.email}
                name="email"
                required
                fullWidth
                placeholder="Enter email address"
                error={Boolean(touched.email && errors.email)}
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel className="required" id="role">
                Role
              </InputLabel>
              <Select
                labelId="role"
                id="role"
                name="role"
                fullWidth
                value={values.role}
                onChange={handleChange}
                onBlur={handleBlur}>
                {roleOptions?.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
              {touched.role && errors.role && (
                <FormHelperText>{errors.role}</FormHelperText>
              )}
            </Grid>
            {values.role !== 'org-manager' && (
              <Grid item xs={12}>
                <InputLabel id="projects">Projects</InputLabel>
                <MultiSelectDropdown
                  name="projects"
                  options={projectOptions}
                  selectedValues={values.projects.map((p) => p.id)}
                  onChange={(selectedIds) => {
                    const updatedProjects = selectedIds.map((id) => {
                      const existing = values.projects.find((p) => p.id === id);
                      const projectInfo = projectOptions.find((p) => p.id === id);
                      return {
                        id,
                        name: projectInfo?.name || 'Unknown',
                        isAdmin: existing?.isAdmin || false,
                        jiraCloudId: projectInfo?.jiraCloudId || null,
                      };
                    });
                    setFieldValue('projects', updatedProjects);
                  }}
                  searchable
                  placeholder="Select Projects"
                  showSelectAll
                />
              </Grid>
            )}
            {values.projects.length > 0 && values?.role === 'team-member' && (
              <Grid item xs={12}>
                <InputLabel id="project-admins">Assign as Project Admin</InputLabel>
                <MultiSelectDropdown
                  name="projectAdmins"
                  options={values.projects.map((p) => ({
                    id: p.id,
                    name: p.name,
                  }))}
                  selectedValues={values.projects
                    .filter((p) => p.isAdmin)
                    .map((p) => p.id)}
                  onChange={(selectedAdminIds) => {
                    const updatedProjects = values.projects.map((project) => ({
                      ...project,
                      isAdmin: selectedAdminIds.includes(project.id),
                    }));
                    setFieldValue('projects', updatedProjects);
                  }}
                  searchable
                  placeholder="Select Project Admins"
                  showSelectAll
                />
              </Grid>
            )}
            {errors.submit && (
              <Grid item xs={12}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Grid>
            )}
            <Grid item xs={12}>
              <Button
                disableElevation
                disabled={isSubmitting || loading}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                sx={{ my: 2 }}
                color="primary">
                {loading ? 'Inviting...' : 'Send Invite'}
              </Button>
            </Grid>
          </Grid>
        </form>
      )}
    </Formik>
  );
};

export default AddUserForm;
